import { EXPERIMENTS, PRIVATE_MIRROR, FEED3, FEED_KIND, PREDICT_FEED, RU_FEED, TRANSFERS } from '../lib/feedContext';
/**
 * Корень нового экрана: сокет, метаданные, секундный тик.
 *
 * Своё подключение, а не общее с `ui/App`. Причина не в изоляции ради изоляции:
 * этот экран разбирает причины смерти вилок, которых старый протокол не
 * присылает, и держит собственный порядок строк. Общий стор означал бы, что
 * любая правка ленты трогает оба экрана сразу, а проверять пришлось бы прод.
 */

import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Notice } from '../access/Notice';
import { pull, push } from '../access/profile';
import { watchBalances } from '../access/balance';
import { useSession } from '../access/session';
import { MIRROR_FEED } from '../lib/build';
import { useSettings } from '../store/settings';
import { FEED_SWITCH, bindFeedHistory, useFeedNav } from './feedSwitch';
import { holdLiveFeed, hydrateLastFrame, liveFeed, noteWatch, stopLiveFeed, wakeLiveFeed } from './liveFeed';
import { takeSeed } from './forkSeed';
import { Feed } from './Feed';
import { loadLenta5Meta, loadMeta, mergeMetaBooks } from './meta';
import { flushProxySecrets } from './accounts';
import { parked } from './parked';
import './parked.css';
import { constrainLiveBooks } from './seen';
import { venueCanon } from './venues';
import './next.css';
import './bands.css';
import { One } from './One';
import { Permit } from './Permit';
import { isDone } from './permit';
import { canonTapes } from './tapes';
import { useFilters } from './filters';
import { LAB_BUNDLE, appHref, forkParam, rootClass } from './route';
import { Showcase } from './Showcase';
import { SightFlash } from './SightFlash';
import { watchIds, useScreen } from './store';
import { BandContext, useBand } from './useBand';
import { DeskCall } from './DeskCall';
import { PhoneBoot } from './phone/PhoneBoot';
import { isPilot } from './dry';
import { PilotHead, PilotFeedBar } from './PilotHead';

/** Как часто перечитывать метаданные. Свежесть и курс меняются медленнее ленты. */
const META_INTERVAL = 30_000;

/** Полоса кассы только в бандле /lab: тестерская сборка этот чанк не несёт. */
const Strip = LAB_BUNDLE
  ? lazy(async () => {
      const mod = await import('../book/Strip');
      return { default: mod.Strip };
    })
  : null;

export function Screen(): React.ReactElement {
  // Адрес читается один раз: окно вилки не переключается в ленту, его закрывают.
  const [only] = useState(() => {
    const id = forkParam(location.search);
    if (id !== null) {
      const seed = takeSeed(id);
      if (seed !== null) useScreen.getState().apply(new Map([[id, seed]]));
    }
    return id;
  });
  // Первый заход на сканер, не на окно вилки: там место стоит дороже, а разрешение
  // на всплывающие окна живёт у сайта целиком — его спрашивают на /app.
  const [permit, setPermit] = useState(() => only === null && !isDone());

  useLayoutEffect(() => {
    const requestedMode = new URLSearchParams(location.search).get('mode');
    if (!TRANSFERS && !RU_FEED && !PREDICT_FEED && (requestedMode === 'feed' || requestedMode === 'showcase')) {
      useScreen.getState().setMode(requestedMode);
    }
    const path = location.pathname.replace(/\/+$/, '');
    if (parked.tests && /(?:^|\/)app\/test$/.test(path)) {
      history.replaceState(null, '', `${appHref()}${location.search}${location.hash}`);
    }
    const linePath = /(?:^|\/)app\/(live|prematch)$/.test(path);
    if (parked.linePages && linePath) {
      history.replaceState(null, '', `${appHref()}${location.search}${location.hash}`);
      if (useScreen.getState().mode !== 'feed') useScreen.getState().setMode('feed');
      return;
    }
    const tapes = new URLSearchParams(location.search).get('tapes');
    if (tapes !== null && tapes !== '') {
      useFilters.getState().apply({ tapes: canonTapes(tapes.split(/[,+]/)) });
      if (useScreen.getState().mode !== 'feed') useScreen.getState().setMode('feed');
    }
    if (!linePath) return;
    if (useScreen.getState().mode !== 'feed') useScreen.getState().setMode('feed');
  }, []);

  useEffect(() => {
    document.body.classList.add('nx-body');
    hydrateLastFrame();
    noteWatch([...watchIds(useScreen.getState())]);
    liveFeed();
    const stopWatch = useScreen.subscribe((state) => {
      noteWatch([...watchIds(state)]);
    });
    const unbindHistory = bindFeedHistory();

    const pullMeta = async (): Promise<void> => {
      const meta = await loadMeta();
      if (meta === null) return;
      const merged =
        FEED_KIND === 'main' ? mergeMetaBooks(meta, await loadLenta5Meta()) : meta;
      useScreen.getState().setMeta(merged);
      useSettings.getState().noteServerFx(merged.fx.usdRub, merged.fx.asOf, merged.fx.stale);
      // /app собран с /v2, поэтому MIRROR_FEED=true, но источники — живые
      // кассы кадра, не ch_a/ch_v. Чужой каталог из 40 имён в сито не пускаем.
      // Пятёрка — свой /v5/v1/meta: без него azuro/shuffle в сите нет.
      constrainLiveBooks(
        parked.ghostVenues && (FEED_KIND === 'main' || !MIRROR_FEED)
          ? merged.sources.map((source) => venueCanon(source.code)).filter((code) => code !== '')
          : null,
      );
    };
    const onFeedSwitch = (): void => {
      void pullMeta();
    };
    window.addEventListener(FEED_SWITCH, onFeedSwitch);
    void pullMeta();
    const meta = setInterval(() => void pullMeta(), META_INTERVAL);

    // Возраст строк идёт локально: сервер присылает его один раз и не гоняет
    // дельту ради тикающего счётчика. В фоне тик молчит: каждый `now` иначе
    // пересобирает ленту каждую секунду, пока человек ставит на чужом сайте.
    let away = false;
    const tick = setInterval(() => {
      if (away) return;
      useScreen.getState().tick(Date.now());
    }, 1000);

    // Чужой сайт — соседняя вкладка, не закрытие. Chrome режет таймеры, сокет
    // при этом живой, и без `hold` ведущий фронт льёт каждый кадр вхолостую.
    // По возврату — один кадр и одна перерисовка слоёв `content-visibility`:
    // Chrome их иногда оставляет чёрными, пока не ткнёшь. Сетку порядка и
    // ведущий фронт на видимой вкладке это не трогает.
    const onVis = (): void => {
      if (document.visibilityState === 'hidden') {
        away = true;
        holdLiveFeed();
        return;
      }
      away = false;
      if (!wakeLiveFeed()) useScreen.getState().tick(Date.now());
      document.body.classList.add('nx-wake');
      requestAnimationFrame(() => document.body.classList.remove('nx-wake'));
    };
    document.addEventListener('visibilitychange', onVis);

    // Кто смотрит и его регуляторы.
    //
    // Профиль спрашивается ВСЕГДА, а не только у вошедшего. Личность на сервере
    // бывает двух видов: вход через Telegram и имя, которым человек открыл
    // дверь по личному паролю (контур тестеров, где входа нет вовсе). Второго
    // страница о себе не знает и знать не может — заголовок ставит прокси, — и
    // проверка `authenticated` оставляла таких людей с профилем в одном
    // браузере. Смена браузера обнуляла им измеренный темп молча, а молча
    // обнулённый темп рисует «успеешь» там, где не успеешь.
    //
    // Там, где личности нет ни той, ни другой, сервер отвечает 404, `pull`
    // возвращает false, и отправка не заводится: лишних запросов не появляется.
    let stopPush: (() => void) | null = null;
    let stopBalances: (() => void) | null = null;
    const whoami = async (): Promise<void> => {
      await useSession.getState().load();
      if (await pull()) {
        stopPush = push();
        stopBalances = watchBalances();
      }
      flushProxySecrets();
    };
    void whoami();

    return () => {
      unbindHistory();
      window.removeEventListener(FEED_SWITCH, onFeedSwitch);
      stopWatch();
      clearInterval(meta);
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVis);
      document.body.classList.remove('nx-wake');
      stopPush?.();
      stopBalances?.();
      stopLiveFeed();
      document.body.classList.remove('nx-body');
    };
  }, []);

  useFeedNav((s) => s.n);
  const mode = useScreen((s) => s.mode);
  const shelf = useScreen((s) => s.shelf);
  const rootRef = useRef<HTMLDivElement>(null);
  const band = useBand(rootRef);

  const pilotId = useSession((s) => s.me.id);
  const pilotName = useSession((s) => s.me.username);
  const pilot = isPilot({ id: pilotId, username: pilotName });

  // Одна вилка отдельным окном: то же приложение, тот же сокет, но без ленты,
  // фильтров и шапки — окно кладут поверх сайта конторы, и место в нём стоит
  // дороже всего (08.08, владелец). Проверяется до режимов: адрес главнее
  // запомненного выбора «витрина или лента». Полоса — от ширины этого окна.
  if (only !== null) {
    return (
      <BandContext.Provider value={band}>
        <div ref={rootRef} className={rootClass(location.pathname, true)}>
          <PhoneBoot />
          <SightFlash />
          {!PRIVATE_MIRROR && <DeskCall />}
          <One id={only} />
        </div>
      </BandContext.Provider>
    );
  }

  // Два режима одного движка, а не две страницы: витрина отвечает на «что
  // делать сейчас», лента — на «что вообще есть». Выбор запоминается, потому
  // что переключать его каждое утро — налог на нерешительность автора.
  return (
    <BandContext.Provider value={band}>
      <div ref={rootRef} className={rootClass(location.pathname, false, pilot)}>
        <PhoneBoot />
        <SightFlash />
        {permit && <Permit onDone={() => setPermit(false)} />}
        <PilotHead modes={['showcase', 'feed']} where="app" />
        {Strip !== null && !parked.book && (
          <Suspense fallback={null}>
            <Strip />
          </Suspense>
        )}
        {!PRIVATE_MIRROR && <DeskCall />}
        <Notice />
        {TRANSFERS || RU_FEED || PREDICT_FEED || EXPERIMENTS || FEED3 || shelf === 'hidden' || mode !== 'showcase' ? (
          <section className="nx-pro">
            <div className="nx-wide">
              <PilotFeedBar />
              <Feed />
            </div>
          </section>
        ) : (
          <Showcase />
        )}
      </div>
    </BandContext.Provider>
  );
}
